import React, { useState, useMemo, useEffect, useCallback } from "react";

/* ============================================================
   로딱이네 — 로또 번호 생성 웹 서비스 (프론트엔드 / API 연동판)

   아키텍처: React ── REST ──> Spring Boot(게이트웨이) ──> FastAPI(생성 엔진)
   - 번호 생성 연산·패턴 판정·목업 데이터는 모두 백엔드로 이전됨
   - 프론트는 표시(View)와 상태(UX)만 담당

   [백엔드 API 규격 — Spring Boot DTO 참고용]

   1) POST /api/lotto/generate
      요청  { count: number, mode: "auto" | "manual",
              want: { A:bool, S:bool, B:bool, C:bool, D:bool, E:bool } }
      응답  { items: [ { nums: number[6], tags: string[] } ] }

   2) GET /api/lotto/flow?limit=60
      응답  [ { round: number, nums: number[6], bonus: number,
               tags: string[] } ]            // tags 예: ["A","B","C"]
             (최신 회차가 배열 앞에 오도록 내림차순 정렬 가정)

   3) GET /api/lotto/results?week={round}&style={roddaki|my}
      응답  { winning: { nums: number[6], bonus: number },
              rows: [ { nums: number[6], match: number,
                        downloads: number, style: string } ],
              summary: { weekSix, weekFive, totalSix,
                         totalFiveBonus, avgGames } }
      ※ 이 엔드포인트는 지시에 없어 임의 규격으로 가정했음.
        확정되면 fetchResults()의 URL/파싱만 맞추면 됨.
   ============================================================ */

/* ---------- API 설정 ---------- */
const API_BASE = "/api/lotto";
/* 개발 시 포트가 다르면(예: React 5173, Spring 8080)
   vite.config.js 에 proxy 설정 권장:
   server: { proxy: { "/api": "http://localhost:8080" } }
   또는 API_BASE = "http://localhost:8080/api/lotto" + Spring CORS 허용 */

/* ---------- 익명 사용자 식별 (UUID) ----------
   로그인 없는 서비스에서 '기기+브라우저' 단위로 사용자를 구별한다.
   첫 방문 시 UUID를 발급해 localStorage에 저장하고,
   모든 API 요청에 X-User-Id 헤더로 동봉한다.
   서버는 이 값으로 인당 10개 제한 검증과 보관함 조회를 수행한다. */
const LS_USER_ID = "roddaki.userId";
function getUserId() {
  let id = null;
  try { id = window.localStorage.getItem(LS_USER_ID); } catch { /* 무시 */ }
  if (!id) {
    id = (window.crypto && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`; // 구형 브라우저 폴백
    try { window.localStorage.setItem(LS_USER_ID, id); } catch { /* 무시 */ }
  }
  return id;
}

async function apiFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: {
      "Content-Type": "application/json",
      "X-User-Id": getUserId(),
    },
    ...options,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

const postGenerate = (body) =>
  apiFetch("/generate", { method: "POST", body: JSON.stringify(body) });
const getFlow = (limit = 60, signal) =>
  apiFetch(`/flow?limit=${limit}`, { signal });
const getResults = (week, style, signal) =>
  apiFetch(`/results?week=${week}&style=${style}`, { signal });
const getMyTickets = (signal) =>
  apiFetch(`/my-tickets`, { signal }); // 이번 주(다음 추첨 회차) 내 티켓 목록

/* ---------- localStorage 유틸 (실패해도 앱이 죽지 않게 래핑) ---------- */
const store = {
  get(key, fallback) {
    try {
      const v = window.localStorage.getItem(key);
      return v == null ? fallback : JSON.parse(v);
    } catch {
      return fallback;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      /* 프라이빗 모드·미지원 환경에서는 메모리 상태로만 동작 */
    }
  },
};
const LS_SAVED = "roddaki.saved";           // 내 번호 보관함
const LS_RECEIVED = "roddaki.receivedWeek"; // 금주 번호 받기 여부
const LS_ADULT = "roddaki.adultUntil";      // 성인 인증 모달 7일 숨김 (epoch ms)

/* ---------- 공용: 로또 볼 색상 (동행복권 표준) ---------- */
const ballColor = (n) =>
  n <= 10 ? "#FBC400" : n <= 20 ? "#69C8F2" : n <= 30 ? "#FF7272" : n <= 40 ? "#AAAAAA" : "#B0D840";

const Ball = ({ n, size = 34, dim = false }) => (
  <span
    style={{
      width: size, height: size, lineHeight: `${size}px`,
      background: dim ? "#E5E7EB" : ballColor(n),
      color: dim ? "#9CA3AF" : "#fff",
      fontSize: size * 0.44, fontWeight: 700,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      borderRadius: "50%", boxShadow: dim ? "none" : "inset -3px -4px 6px rgba(0,0,0,.18)",
    }}
  >
    {String(n).padStart(2, "0")}
  </span>
);

const TAG_META = [
  { key: "A", name: "바이킹 현상", short: "바이킹", color: "#F59E0B" },
  { key: "S", name: "연속된 정수", short: "연속수", color: "#3B82F6" },
  { key: "B", name: "엔딩 하모니", short: "엔딩", color: "#EF4444" },
  { key: "C", name: "이월수 현상", short: "이월수", color: "#10B981" },
];

/* ============================================================
   공용 UI
   ============================================================ */
const AdSlot = ({ label = "광고", h = 90 }) => (
  <div
    className="flex items-center justify-center rounded-lg text-sm font-medium"
    style={{ height: h, background: "repeating-linear-gradient(45deg,#F3F4F6,#F3F4F6 12px,#E5E7EB 12px,#E5E7EB 24px)", color: "#9CA3AF" }}
  >
    {label}
  </div>
);

const Notice = ({ children }) => (
  <p className="text-xs text-gray-500 leading-relaxed">{children}</p>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight mb-6">{children}</h2>
);

const Spinner = ({ size = 20 }) => (
  <span
    className="inline-block animate-spin rounded-full border-2 border-white/40 border-t-white"
    style={{ width: size, height: size }}
  />
);

const LoadingBlock = ({ text = "데이터를 불러오는 중이에요…" }) => (
  <div className="rounded-2xl border border-gray-100 bg-white p-12 text-center text-gray-400 font-medium">
    <span className="inline-block animate-spin rounded-full border-2 border-gray-200 border-t-teal-500 w-6 h-6 mb-3" />
    <p>{text}</p>
  </div>
);

const ErrorBlock = ({ error, onRetry }) => (
  <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-center">
    <p className="font-bold text-red-500">데이터를 불러오지 못했어요.</p>
    <p className="text-xs text-red-400 mt-1 break-all">{String(error?.message || error)}</p>
    {onRetry && (
      <button onClick={onRetry}
        className="mt-4 px-6 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold">
        다시 시도
      </button>
    )}
  </div>
);

/* 반원 게이지 */
const Gauge = ({ pct, color, label }) => {
  const r = 54, c = Math.PI * r;
  return (
    <div className="flex flex-col items-center">
      <svg width="140" height="84" viewBox="0 0 140 84">
        <path d="M 16 76 A 54 54 0 0 1 124 76" fill="none" stroke="#EEF0F3" strokeWidth="16" strokeLinecap="round" />
        <path
          d="M 16 76 A 54 54 0 0 1 124 76" fill="none" stroke={color} strokeWidth="16" strokeLinecap="round"
          strokeDasharray={`${(pct / 100) * c} ${c}`}
        />
        <text x="70" y="70" textAnchor="middle" fontSize="22" fontWeight="800" fill="#1F2937">{pct}%</text>
      </svg>
      <div className="text-sm font-bold text-gray-700 mt-1 whitespace-pre-line text-center">{label}</div>
    </div>
  );
};

const TagChip = ({ k }) => {
  const m = TAG_META.find((t) => t.key === k);
  if (!m) return null;
  return (
    <span className="inline-flex items-center justify-center rounded-full text-white text-[11px] font-bold"
      style={{ width: 20, height: 20, background: m.color }}>{k}</span>
  );
};

/* ---------- 공용 훅: 패턴 FLOW 데이터 (3·4·6페이지 공유) ---------- */
function useFlow(limit = 60) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setLoading(true);
    setError(null);
    getFlow(limit, ctrl.signal)
      .then((rows) => setData(rows))
      .catch((e) => {
        if (e.name !== "AbortError") setError(e);
      })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [limit, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);
  return { data, loading, error, retry };
}

/* ============================================================
   첫화면 (성인 인증 + 소개)
   ============================================================ */
const LANDING_DEMO = [
  [3, 11, 17, 24, 33, 42],
  [7, 8, 19, 26, 31, 45],
  [2, 14, 22, 28, 36, 41],
  [5, 13, 21, 30, 39, 44],
  [9, 12, 18, 27, 35, 40],
]; // 배경 연출용 정적 번호 (연산 없음)

function Landing({ onGo, adult, confirmAdult }) {
  const [hide7, setHide7] = useState(false);
  return (
    <div className="relative min-h-[calc(100vh-64px)] bg-white overflow-hidden">
      {/* 배경: 흐릿한 번호받기 화면 연출 */}
      <div className="absolute right-0 top-0 bottom-0 w-1/2 hidden lg:flex flex-col gap-4 p-12 opacity-25 blur-[2px] pointer-events-none select-none">
        {LANDING_DEMO.map((row, r) => (
          <div key={r} className="flex gap-3 items-center">
            <span className="text-gray-400 font-bold w-8">{r + 1}</span>
            {row.map((n) => <Ball key={n} n={n} size={40} />)}
          </div>
        ))}
        <div className="mt-4 w-48 h-12 rounded-xl bg-teal-500" />
      </div>

      <div className="relative max-w-6xl mx-auto px-6 py-16">
        <p className="text-teal-600 font-bold mb-3">로또 번호 생성 서비스</p>
        <h1 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight">
          로또 상위 판매점의 게임수 데이터와<br />
          6수 전체 일치 관계를 분석하여 웹으로 구현
        </h1>
        <ul className="mt-8 space-y-2 text-lg text-gray-700 font-medium">
          <li>· 90회 이상 6수 전체 일치 패턴 적용</li>
          <li>· 회원가입 없이 무료로 이용 가능</li>
          <li>· 모든 결과 공개, 개인 검증 가능</li>
        </ul>

        <div className="mt-8 inline-block rounded-2xl bg-teal-50 border border-teal-100 px-6 py-4 text-teal-800 font-semibold">
          2026년 7월 7일 오픈 이후 아직 6수 전체 일치가 없어요. 함께 응원해 주세요!
        </div>

        <div className="mt-10 flex gap-4">
          <button onClick={() => onGo(4)}
            className="px-8 py-4 rounded-2xl bg-teal-500 hover:bg-teal-600 text-white text-xl font-extrabold shadow-lg shadow-teal-200 transition">
            번호 받기
          </button>
          <button onClick={() => onGo(6)}
            className="px-8 py-4 rounded-2xl bg-white border-2 border-teal-500 text-teal-600 hover:bg-teal-50 text-xl font-extrabold transition">
            결과 조회
          </button>
        </div>
        <p className="mt-10 text-sm text-gray-400">본 서비스는 19세 미만 청소년의 이용을 제한합니다.</p>
      </div>

      {/* 성인 인증 모달 */}
      {!adult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center">
            <div className="text-5xl mb-4">🔞</div>
            <h3 className="text-xl font-extrabold text-gray-900">만 19세 이상이십니까?</h3>
            <p className="mt-3 text-sm text-gray-500 leading-relaxed">
              본 서비스는 19세 미만 청소년의 이용을 제한합니다.<br />
              로또는 소액으로 건전하게 즐기세요.
            </p>
            <div className="mt-6 flex gap-3">
              <button onClick={() => confirmAdult(hide7)}
                className="flex-1 py-3 rounded-xl bg-teal-500 hover:bg-teal-600 text-white font-bold">
                예, 19세 이상입니다
              </button>
              <button onClick={() => window.history.back()}
                className="flex-1 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-600 font-bold">
                아니오
              </button>
            </div>
            <label className="mt-4 inline-flex items-center gap-2 text-sm text-gray-500 cursor-pointer">
              <input type="checkbox" checked={hide7} onChange={(e) => setHide7(e.target.checked)} />
              7일간 보지 않기
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   1페이지 — 기능 소개  (정적 콘텐츠, API 불필요)
   ============================================================ */
function Page1() {
  const cards = [
    {
      title: "편리하게", emoji: "🛵",
      lines: [
        "90회 이상 일치 패턴을 분석한 조합으로 가상 매장 '로딱이네'를 도입, 오프라인 이용 시 발생하는 불편 해소",
        "회원가입·로그인 없는 퀵패스 서비스",
        "단계별 의사결정을 지원하는 정보 제공",
      ],
    },
    {
      title: "신뢰하며", emoji: "🤝",
      lines: [
        "모든 생성 데이터의 투명한 공개",
        "조작 의혹 방지를 위해 '로딱이네' 생성 데이터 다운로드 가능",
      ],
    },
    {
      title: "무료로", emoji: "🆓",
      lines: [
        "개인정보 누출 방지를 위해 비회원제·미결제 준수",
        "실구매 및 이익 실현은 이용자의 선택",
      ],
    },
    {
      title: "함께 하기", emoji: "🐣",
      lines: [
        "감성 캐릭터 로딱이(로딱이네)와 함께 하는 롤플레잉",
        "자율성이 부여된 나만의 패턴 조합 설계",
      ],
    },
  ];
  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <SectionTitle>기능 소개</SectionTitle>
      <div className="grid md:grid-cols-2 gap-6">
        {cards.map((c) => (
          <div key={c.title} className="rounded-2xl border border-gray-100 bg-white shadow-sm p-7 hover:shadow-md transition">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-3xl">{c.emoji}</span>
              <h3 className="text-xl font-extrabold text-teal-600">{c.title}</h3>
            </div>
            <ul className="space-y-2 text-gray-600 leading-relaxed">
              {c.lines.map((l, i) => <li key={i}>· {l}</li>)}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-12 rounded-2xl bg-teal-50 p-8 text-center">
        <div className="text-5xl mb-3">🐥</div>
        <p className="text-xl font-extrabold text-gray-800">
          내 손 안의 작은 즐거움,<br className="md:hidden" /> 당신이 머무는 모든 곳이 1등 명당이에요.
        </p>
      </div>

      <div className="mt-10 space-y-1">
        <Notice>※ 로딱이네 수치는 과거 데이터 분석 결과이며 미래 결과와 무관합니다.</Notice>
        <Notice>※ 생성된 번호가 당첨을 보장하지 않습니다.</Notice>
      </div>
      <footer className="mt-6 border-t border-gray-100 pt-4 text-[11px] text-gray-400 leading-relaxed">
        상호명: 로딱이 네트워크 (042-000-0000) · 주소: 대전광역시 유성구 봉명서로 43-4 (102호) · 사업자 등록번호: 000-00-00000<br />
        통신판매업 신고번호: 제 2026-대전00-0000호 · 저작권: 00-000에 의해 보호받습니다
      </footer>
    </div>
  );
}

/* ============================================================
   2페이지 — 패턴의 이해  (정적 콘텐츠, API 불필요)
   ============================================================ */
function Page2() {
  const select = [
    { pct: 66, name: "바이킹 현상", color: "#F59E0B", desc: "6수 일치 번호의 구간별 개수는 주차별로 증감을 반복", ex: "구간별 개수 의미: 6 9 11 12 31 45 → 2 2 0 1 1" },
    { pct: 52, name: "연속된 정수", color: "#3B82F6", desc: "6수 일치 번호에 연속된 일련번호가 포함", ex: "6 9 11 12 31 45 → 11 12" },
    { pct: 78, name: "엔딩 하모니", color: "#EF4444", desc: "6수 일치 번호 중 2수 이상은 끝수가 동일", ex: "6 9 11 12 31 45 → 11 31" },
    { pct: 61, name: "이월수 현상", color: "#10B981", desc: "전주의 6수 일치 번호 중 1~2개가 이번 주에도 보임", ex: "전주 14 18 21 24 31 38 · 금주 6 9 11 12 31 45 → 31" },
  ];
  const fixed = [
    { pct: 2.6, name: "다양성 필터", desc: "조합번호의 뒷수−앞수의 종류가 9개 이하인 번호", ex: "17 20 29 35 38 44 → 종류가 3, 6에 치중되어 필터됨" },
    { pct: 2.9, name: "짝홀수 필터", desc: "조합번호 6자리 모두 짝수 또는 홀수로 구성된 경우", ex: "2 4 16 20 24 42 → 모두 짝수로 필터됨" },
    { pct: 1.7, name: "연속간격 필터", desc: "조합번호 중 동일 간격이 3회 이상 연속되는 경우", ex: "11 · 14 · 17 · 20 37 38 (다양성 필터와 차이 있음)" },
    { pct: 2.1, name: "이월수 과다 필터", desc: "전주의 6수 일치 번호와 3개 이상 일치하는 번호", ex: "전주 1 4 9 15 18 22 → 1 4 9 27 32 41은 필터됨" },
  ];
  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <SectionTitle>패턴의 이해</SectionTitle>

      <h3 className="text-lg font-extrabold text-teal-600 mb-4">선택형 패턴</h3>
      <div className="grid md:grid-cols-2 gap-5 mb-12">
        {select.map((p) => (
          <div key={p.name} className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6 flex gap-5 items-start">
            <div className="shrink-0 w-20 h-20 rounded-full flex flex-col items-center justify-center text-white font-black"
              style={{ background: p.color }}>
              <span className="text-xl leading-none">{p.pct}%</span>
            </div>
            <div>
              <h4 className="font-extrabold text-gray-800 text-lg">{p.name}</h4>
              <p className="text-gray-600 mt-1 leading-relaxed">{p.desc}</p>
              <p className="text-sm text-gray-400 mt-2">예) {p.ex}</p>
            </div>
          </div>
        ))}
      </div>

      <h3 className="text-lg font-extrabold text-gray-700 mb-1">고정형 패턴</h3>
      <p className="text-sm text-gray-500 mb-4">고정형 패턴은 자동 반영되며 약 7%의 조합번호가 제거됩니다.</p>
      <div className="grid md:grid-cols-2 gap-5">
        {fixed.map((p) => (
          <div key={p.name} className="rounded-2xl border border-gray-100 bg-gray-50 p-6 flex gap-5 items-start">
            <div className="shrink-0 w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center text-white font-black text-lg">
              {p.pct}%
            </div>
            <div>
              <h4 className="font-extrabold text-gray-800 text-lg">{p.name}</h4>
              <p className="text-gray-600 mt-1 leading-relaxed">{p.desc}</p>
              <p className="text-sm text-gray-400 mt-2">예) {p.ex}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   3페이지 — 기간별 패턴 통계 지표  (GET /api/lotto/flow)
   ============================================================ */
function Page3() {
  const [period, setPeriod] = useState("all");
  const [style, setStyle] = useState("gauge"); // gauge | bar
  const { data, loading, error, retry } = useFlow(60);

  const rows = useMemo(() => {
    if (!data) return [];
    if (period === "recent") return data.slice(0, 20);
    if (period === "half") return data.slice(0, 30);
    return data;
  }, [data, period]);

  const stats = useMemo(() => {
    const n = rows.length || 1;
    return TAG_META.map((t) => ({
      ...t,
      pct: Math.round((rows.filter((r) => r.tags?.includes(t.key)).length / n) * 100),
    }));
  }, [rows]);

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <SectionTitle>기간별 패턴 통계 지표</SectionTitle>

      {/* 조회기간 / 그래프 스타일 */}
      <div className="flex flex-wrap gap-4 mb-8">
        <label className="flex items-center gap-2 text-sm font-bold text-gray-600">
          조회 기간
          <select value={period} onChange={(e) => setPeriod(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 font-medium">
            {/* 전체 / 1~500주 / 501~1000주 / 1001주~전주 구간은
                flow API에 from/to 파라미터 추가 후 확장 */}
            <option value="all">전체</option>
            <option value="half">최근 30주</option>
            <option value="recent">최근 20주</option>
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm font-bold text-gray-600">
          그래프 스타일
          <select value={style} onChange={(e) => setStyle(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 font-medium">
            <option value="gauge">반원형</option>
            <option value="bar">막대형</option>
          </select>
        </label>
      </div>

      {loading && <LoadingBlock text="패턴 통계를 불러오는 중이에요…" />}
      {error && <ErrorBlock error={error} onRetry={retry} />}

      {!loading && !error && (
        <>
          {/* 그래프 */}
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-8 mb-10">
            {style === "gauge" ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 justify-items-center">
                {stats.map((s) => <Gauge key={s.key} pct={s.pct} color={s.color} label={s.name.replace(" ", "\n")} />)}
              </div>
            ) : (
              <div className="space-y-4">
                {stats.map((s) => (
                  <div key={s.key} className="flex items-center gap-4">
                    <span className="w-24 text-sm font-bold text-gray-700">{s.name}</span>
                    <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full flex items-center justify-end pr-2 text-[11px] text-white font-bold"
                        style={{ width: `${s.pct}%`, background: s.color }}>{s.pct}%</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* 주차별 테이블 */}
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="grid grid-cols-[70px_1fr_repeat(4,56px)] items-center px-5 py-3 bg-gray-50 text-xs font-extrabold text-gray-500 sticky top-0">
              <span>주차</span><span>당첨번호</span>
              {TAG_META.map((t) => <span key={t.key} className="text-center">{t.short}</span>)}
            </div>
            <div className="max-h-96 overflow-y-auto divide-y divide-gray-50">
              {rows.map((r) => (
                <div key={r.round} className="grid grid-cols-[70px_1fr_repeat(4,56px)] items-center px-5 py-2.5">
                  <span className="font-bold text-gray-700">{r.round}</span>
                  <span className="flex gap-1.5 flex-wrap">{r.nums.map((n) => <Ball key={n} n={n} size={28} />)}</span>
                  {TAG_META.map((t) => (
                    <span key={t.key} className="text-center">
                      {r.tags?.includes(t.key) ? <TagChip k={t.key} /> : <span className="text-gray-200">·</span>}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/* 번호 받기 가능 시간 판정 (서버와 동일 규칙, KST 기준) — Page4 컴포넌트 위쪽에 추가 */
function isGenerationOpen() {
  const kst = new Date(Date.now() + (9 * 60 + new Date().getTimezoneOffset()) * 60000);
  const day = kst.getDay();   // 0=일, 6=토
  const hour = kst.getHours();
  if (day === 6 && hour >= 20) return false;
  if (day === 0 && hour < 6) return false;
  return true;
}

/* ============================================================
   4페이지 — 번호 받기  (POST /api/lotto/generate)
   ============================================================ */
function Page4({ saved, addNumbers, receivedThisWeek, setReceivedThisWeek }) {
  const [roddakiCount, setRoddakiCount] = useState(2);
  const [myCount, setMyCount] = useState(0);
  const [myTags, setMyTags] = useState([]);
  const [warn, setWarn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [genError, setGenError] = useState(null);
  const MAX = 10;
  const remaining = MAX - saved.length;
  const total = roddakiCount + myCount;
  const open = isGenerationOpen();          // ← 추가

  const { data: flow, loading: flowLoading, error: flowError, retry } = useFlow(20);

  const toggleTag = (k) =>
    setMyTags((t) => (t.includes(k) ? t.filter((x) => x !== k) : [...t, k]));

  /* want 객체 빌드: 선택 태그만 true, D·E는 서버 내부 로직(비공개)이라 항상 false */
  const buildWant = (tags) => ({
    A: tags.includes("A"), S: tags.includes("S"),
    B: tags.includes("B"), C: tags.includes("C"),
    D: false, E: false,
  });

  const handleGenerate = async () => {
    if (isLoading || total <= 0) return;
    if (total > remaining) { setWarn(true); return; }
    setWarn(false);
    setGenError(null);
    setIsLoading(true);
    try {
      const requests = [];
      if (roddakiCount > 0) {
        requests.push(
          postGenerate({ count: roddakiCount, mode: "auto", want: buildWant([]) })
            .then((res) => (res.items || []).map((it) => ({ ...it, style: "roddaki" })))
        );
      }
      if (myCount > 0) {
        requests.push(
          postGenerate({ count: myCount, mode: "manual", want: buildWant(myTags) })
            .then((res) => (res.items || []).map((it) => ({ ...it, style: "my" })))
        );
      }
      const results = (await Promise.all(requests)).flat();
      if (results.length === 0) throw new Error("생성된 번호가 없습니다. 조건을 조정해 보세요.");
      addNumbers(results);
      setReceivedThisWeek(true);
    } catch (e) {
      setGenError(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12 grid lg:grid-cols-[1.2fr_1fr] gap-10">
      {/* 좌측: 최근 20주 패턴 FLOW */}
      <div>
        <SectionTitle>최근 20주 패턴 FLOW</SectionTitle>
        {flowLoading && <LoadingBlock text="최근 20주 패턴을 불러오는 중이에요…" />}
        {flowError && <ErrorBlock error={flowError} onRetry={retry} />}
        {!flowLoading && !flowError && (
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="grid grid-cols-[70px_1fr_repeat(4,52px)] items-center px-5 py-3 bg-gray-50 text-xs font-extrabold text-gray-500">
              <span>주차</span><span>당첨번호</span>
              {TAG_META.map((t) => <span key={t.key} className="text-center">{t.short}</span>)}
            </div>
            <div className="max-h-[520px] overflow-y-auto divide-y divide-gray-50">
              {(flow || []).map((r) => (
                <div key={r.round} className="grid grid-cols-[70px_1fr_repeat(4,52px)] items-center px-5 py-2.5">
                  <span className="font-bold text-gray-700">{r.round}</span>
                  <span className="flex gap-1.5 flex-wrap">{r.nums.map((n) => <Ball key={n} n={n} size={26} />)}</span>
                  {TAG_META.map((t) => (
                    <span key={t.key} className="text-center">
                      {r.tags?.includes(t.key) ? <TagChip k={t.key} /> : <span className="text-gray-200">·</span>}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 우측: 번호 받기 패널 */}
      <div>
        <SectionTitle>번호 받기</SectionTitle>
        <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-7 space-y-6">
          {warn ? (
            <p className="font-extrabold text-red-500 leading-relaxed" style={{ fontSize: "1.2em" }}>
              번호 받기 가능 수량은 인당 10개까지입니다.
            </p>
          ) : (
            <div className="space-y-1">
              <Notice>※ 번호 받기 가능 시간: 일요일 06시 ~ 토요일 20시</Notice>
              <Notice>※ 번호 받기 가능 수량: 10개 (인당) — 현재 {saved.length}개 사용, {remaining}개 남음</Notice>
              <Notice>※ 내 맘대로 고르기를 하는 경우 패턴의 이해 → 최근 20주 패턴 FLOW를 참조하세요.</Notice>
            </div>
          )}

          {/* 로딱이네 (mode: auto) */}
          <div className="rounded-xl bg-teal-50 border border-teal-100 p-5">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-extrabold text-teal-700">📤 로딱이네 이용하기</p>
                <p className="text-xs text-teal-600 mt-1">90회 이상 일치 패턴을 분석한 추천 조합</p>
              </div>
              <input type="number" min={0} max={MAX} value={roddakiCount} disabled={isLoading}
                onChange={(e) => setRoddakiCount(Math.max(0, Math.min(MAX, Number(e.target.value) || 0)))}
                className="w-16 text-center border border-teal-200 rounded-lg py-2 font-bold disabled:opacity-50" />
            </div>
          </div>

          {/* 내 맘대로 (mode: manual) */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <p className="font-extrabold text-gray-700">📤 내 맘대로 고르기</p>
              <input type="number" min={0} max={MAX} value={myCount} disabled={isLoading}
                onChange={(e) => setMyCount(Math.max(0, Math.min(MAX, Number(e.target.value) || 0)))}
                className="w-16 text-center border border-gray-200 rounded-lg py-2 font-bold disabled:opacity-50" />
            </div>
            <div className="flex flex-wrap gap-2">
              {TAG_META.map((t) => (
                <button key={t.key} onClick={() => toggleTag(t.key)} disabled={isLoading}
                  className={`px-3 py-1.5 rounded-full text-sm font-bold border transition disabled:opacity-50 ${
                    myTags.includes(t.key)
                      ? "text-white border-transparent"
                      : "bg-white text-gray-500 border-gray-200"
                  }`}
                  style={myTags.includes(t.key) ? { background: t.color } : {}}>
                  💠 {t.short}
                </button>
              ))}
            </div>
          </div>

          {genError && (
            <p className="text-sm font-bold text-red-500">
              번호 생성에 실패했어요. 잠시 후 다시 시도해 주세요.
              <span className="block text-xs font-normal text-red-400 mt-1 break-all">{String(genError.message)}</span>
            </p>
          )}

          <button onClick={handleGenerate} disabled={total <= 0 || isLoading || !open}
            className={`w-full py-4 rounded-2xl text-xl font-extrabold text-white shadow-lg transition flex items-center justify-center gap-3 ${
              receivedThisWeek ? "bg-amber-500 hover:bg-amber-600 shadow-amber-200" : "bg-teal-500 hover:bg-teal-600 shadow-teal-200"
            } disabled:opacity-40 disabled:cursor-not-allowed`}>
            {!open ? (
              "지금은 번호 받기 시간이 아니에요 (일 06시~토 20시)"
            ) : isLoading ? (
              <>
                <Spinner /> 번호를 생성하고 있어요…
              </>
            ) : receivedThisWeek ? (
              "재확인 (내 번호 보관함)"
            ) : (
              "번호 받기 · 생성"
            )}
          </button>

          <div className="space-y-1 pt-1">
            <Notice>※ 로딱이네 수치는 과거 데이터 분석 결과이며 미래 결과와 무관합니다.</Notice>
            <Notice>※ 생성된 번호가 당첨을 보장하지 않습니다.</Notice>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   5페이지 — 내 번호 보관함  (localStorage 영속화)
   ============================================================ */
function Page5({ saved, onGo }) {
  const groups = [
    { key: "roddaki", name: "로딱이네", accent: "border-teal-200 bg-teal-50/50" },
    { key: "my", name: "내 맘대로", accent: "border-amber-200 bg-amber-50/50" },
  ];
  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <SectionTitle>내 번호 보관함</SectionTitle>

      {saved.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 p-14 text-center">
          <div className="text-4xl mb-3">📭</div>
          <p className="text-gray-500 font-medium">아직 받은 번호가 없어요. 번호 받기에서 이번 주 조합을 생성해 보세요.</p>
          <button onClick={() => onGo(4)}
            className="mt-6 px-8 py-3 rounded-2xl bg-teal-500 hover:bg-teal-600 text-white font-extrabold shadow-lg shadow-teal-200">
            생성하러 가기
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => {
            const list = saved.filter((s) => s.style === g.key);
            if (list.length === 0) return null;
            return (
              <div key={g.key} className={`rounded-2xl border p-6 ${g.accent}`}>
                <h3 className="font-extrabold text-gray-800 mb-4">{g.name} <span className="text-gray-400 font-bold">({list.length})</span></h3>
                <div className="space-y-3">
                  {list.map((s, i) => (
                    <div key={i} className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 px-4 py-3">
                      <span className="text-gray-400 font-bold w-6">{i + 1}</span>
                      <span className="flex gap-2 flex-wrap">{s.nums.map((n) => <Ball key={n} n={n} size={32} />)}</span>
                      <span className="ml-auto flex gap-1">{(s.tags || []).map((t) => <TagChip key={t} k={t} />)}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <div className="text-center">
            <button onClick={() => onGo(4)}
              className="px-10 py-4 rounded-2xl bg-teal-500 hover:bg-teal-600 text-white text-lg font-extrabold shadow-lg shadow-teal-200">
              생성
            </button>
          </div>
        </div>
      )}

      <div className="mt-10 space-y-1">
        <Notice>※ 로또는 소액으로 건전하게 즐기세요.</Notice>
        <Notice>※ 로딱이네 수치는 과거 데이터 분석 결과이며 미래 결과와 무관합니다.</Notice>
        <Notice>※ 생성된 번호가 당첨을 보장하지 않습니다.</Notice>
      </div>
      <div className="mt-6"><AdSlot label="앵커 광고" h={70} /></div>
    </div>
  );
}

/* ============================================================
   6페이지 — 번호 추첨 결과  (GET /api/lotto/results)
   ============================================================ */
function Page6() {
  const [tab, setTab] = useState("roddaki");
  const { data: flow, loading: flowLoading, error: flowError, retry } = useFlow(10);
  const weeks = useMemo(() => (flow || []), [flow]); // 결과 확정된 과거 주차
  const [week, setWeek] = useState(null);

  useEffect(() => {
    if (weeks.length > 0 && week == null) setWeek(weeks[0].round);
  }, [weeks, week]);

  const [result, setResult] = useState(null);
  const [resLoading, setResLoading] = useState(false);
  const [resError, setResError] = useState(null);
  const [resKey, setResKey] = useState(0);

  useEffect(() => {
    if (week == null) return;
    const ctrl = new AbortController();
    setResLoading(true);
    setResError(null);
    getResults(week, tab, ctrl.signal)
      .then(setResult)
      .catch((e) => { if (e.name !== "AbortError") setResError(e); })
      .finally(() => setResLoading(false));
    return () => ctrl.abort();
  }, [week, tab, resKey]);

  const winning = result?.winning;
  const rows = result?.rows || [];
  const summary = result?.summary || {};

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <SectionTitle>번호 추첨 결과</SectionTitle>

      {/* 탭 + 주차 */}
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <div className="flex rounded-xl bg-gray-100 p-1">
          {[["roddaki", "로딱이네 스타일"], ["my", "내 맘대로 스타일"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-5 py-2 rounded-lg font-bold text-sm transition ${tab === k ? "bg-white shadow text-teal-600" : "text-gray-500"}`}>
              {label}
            </button>
          ))}
        </div>
        <label className="flex items-center gap-2 text-sm font-bold text-gray-600 ml-auto">
          조회 주차
          <select value={week ?? ""} onChange={(e) => setWeek(Number(e.target.value))}
            className="border border-gray-200 rounded-lg px-3 py-2 font-medium" disabled={weeks.length === 0}>
            {weeks.map((w) => <option key={w.round} value={w.round}>{w.round}회</option>)}
          </select>
        </label>
      </div>

      {(flowLoading || (resLoading && !result)) && <LoadingBlock text="추첨 결과를 불러오는 중이에요…" />}
      {flowError && <ErrorBlock error={flowError} onRetry={retry} />}
      {resError && <ErrorBlock error={resError} onRetry={() => setResKey((k) => k + 1)} />}

      {!flowLoading && !flowError && !resError && result && (
        <div className={`grid lg:grid-cols-[1.5fr_1fr] gap-8 ${resLoading ? "opacity-50 pointer-events-none" : ""}`}>
          {/* 좌측: 결과 테이블 */}
          <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 flex items-center gap-2 text-sm font-extrabold text-gray-600">
              {week}회 당첨번호
              {winning && (
                <>
                  <span className="flex gap-1.5 ml-2">{winning.nums.map((n) => <Ball key={n} n={n} size={26} />)}</span>
                  <span className="text-gray-400">+</span><Ball n={winning.bonus} size={26} />
                </>
              )}
            </div>
            <div className={`grid ${tab === "roddaki" ? "grid-cols-[44px_1fr_84px_72px]" : "grid-cols-[44px_1fr_84px_90px]"} px-5 py-3 text-xs font-extrabold text-gray-500 border-b border-gray-50`}>
              <span>구분</span><span>생성 번호</span><span className="text-center">6수 일치</span>
              <span className="text-center">{tab === "roddaki" ? "다운로드" : "스타일"}</span>
            </div>
            <div className="max-h-[440px] overflow-y-auto divide-y divide-gray-50">
              {rows.length === 0 && (
                <p className="px-5 py-10 text-center text-gray-400 font-medium">해당 주차의 생성 데이터가 없어요.</p>
              )}
              {rows.map((r, i) => (
                <div key={i} className={`grid ${tab === "roddaki" ? "grid-cols-[44px_1fr_84px_72px]" : "grid-cols-[44px_1fr_84px_90px]"} items-center px-5 py-2.5`}>
                  <span className="font-bold text-gray-500">{i + 1}</span>
                  <span className="flex gap-1.5 flex-wrap">
                    {r.nums.map((n) => <Ball key={n} n={n} size={28} dim={!winning?.nums?.includes(n)} />)}
                  </span>
                  <span className={`text-center font-extrabold ${r.match >= 3 ? "text-red-500" : "text-gray-600"}`}>{r.match}개</span>
                  <span className="text-center text-sm font-bold text-gray-500">
                    {tab === "roddaki" ? `${r.downloads}회` : r.style}
                  </span>
                </div>
              ))}
            </div>
            <div className="px-5 py-3 bg-gray-50 text-right">
              {/* 생성 데이터 CSV 다운로드: GET /api/lotto/results/download?week= */}
              <a href={`${API_BASE}/results/download?week=${week}&style=${tab}`}
                className="text-sm font-bold text-teal-600 hover:underline">⬇ 생성 데이터 다운로드</a>
            </div>
          </div>

          {/* 우측: 일치 현황 패널 */}
          <div className="space-y-5">
            <div className="rounded-2xl border border-gray-100 bg-white shadow-sm p-6">
              <h3 className="font-extrabold text-gray-800 mb-4">[{week}] 주차 6수 일치 현황</h3>
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-gray-500 font-bold">6수 부합도</span>
                  <span className="text-3xl font-black text-teal-600">{summary.weekSix ?? 0}회</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-gray-500 font-bold">5수 + 보너스</span>
                  <span className="text-3xl font-black text-gray-700">{summary.weekFive ?? 0}회</span>
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-gray-100 bg-teal-50/60 p-6">
              <h3 className="font-extrabold text-gray-800">누계 일치 현황</h3>
              <p className="text-xs text-gray-400 mb-4">(26. 7. 7 OPEN 이후)</p>
              <div className="space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-gray-500 font-bold">6수 부합도</span>
                  <span className="text-3xl font-black text-teal-600">{summary.totalSix ?? 0}회</span>
                </div>
                <div className="flex items-baseline justify-between">
                  <span className="text-gray-500 font-bold">5수 + 보너스</span>
                  <span className="text-3xl font-black text-gray-700">{summary.totalFiveBonus ?? 0}회</span>
                </div>
                <div className="flex items-baseline justify-between pt-2 border-t border-teal-100">
                  <span className="text-gray-500 font-bold">평균 게임수</span>
                  <span className="text-xl font-black text-gray-700">{(summary.avgGames ?? 0).toLocaleString()}</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <AdSlot h={72} /><AdSlot h={72} /><AdSlot h={72} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
   App — 네비게이션 + 전역 상태 (localStorage 영속화)
   ============================================================ */
const NAV = [
  { id: 0, label: "홈" },
  { id: 1, label: "기능소개" },
  { id: 2, label: "패턴의 이해" },
  { id: 3, label: "패턴 통계" },
  { id: 4, label: "번호 받기" },
  { id: 5, label: "내 번호 보관함" },
  { id: 6, label: "결과 조회" },
];

export default function RoddakiApp() {
  const [page, setPage] = useState(0);

  /* 성인 인증: '7일간 보지 않기' 체크 시 만료 시각을 저장 */
  const [adult, setAdult] = useState(() => {
    const until = store.get(LS_ADULT, 0);
    return Date.now() < until;
  });
  const confirmAdult = (hide7days) => {
    setAdult(true);
    if (hide7days) store.set(LS_ADULT, Date.now() + 7 * 24 * 60 * 60 * 1000);
  };

  /* 내 번호 보관함
     - localStorage 값으로 즉시 표시(오프라인/로딩 중 대비)
     - 서버(/my-tickets)가 진실의 원천: 마운트 시 동기화하며 덮어씀
     - 서버는 '이번 주(다음 추첨 회차)' 티켓만 반환하므로,
       주차가 바뀌면 자동으로 빈 목록이 와서 보관함이 리셋된다 (기존 TODO 해결) */
  const [saved, setSaved] = useState(() => store.get(LS_SAVED, []));
  const [receivedThisWeek, setReceivedThisWeekState] = useState(() => store.get(LS_RECEIVED, false));

  useEffect(() => { store.set(LS_SAVED, saved); }, [saved]);
  const setReceivedThisWeek = (v) => {
    setReceivedThisWeekState(v);
    store.set(LS_RECEIVED, v);
  };

  useEffect(() => {
    const ctrl = new AbortController();
    getMyTickets(ctrl.signal)
      .then((res) => {
        const items = res.items || [];
        setSaved(items);
        setReceivedThisWeekState(items.length > 0);
        store.set(LS_RECEIVED, items.length > 0);
      })
      .catch(() => { /* 서버 미기동/오프라인 시 localStorage 값 유지 */ });
    return () => ctrl.abort();
  }, []);

  const addNumbers = useCallback((rows) => {
    setSaved((s) => [...s, ...rows].slice(0, 10));
    setPage(5); // 생성 후 보관함으로 이동
  }, []);

  const go = (p) => { setPage(p); window.scrollTo(0, 0); };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900" style={{ fontFamily: "'Pretendard','Apple SD Gothic Neo','Noto Sans KR',sans-serif" }}>
      {/* 헤더 */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center gap-6">
          <button onClick={() => go(0)} className="flex items-center gap-2 font-black text-xl text-teal-600 shrink-0">
            <span className="text-2xl">🐥</span> 로딱이네
          </button>
          <nav className="flex gap-1 overflow-x-auto">
            {NAV.slice(1).map((n) => (
              <button key={n.id} onClick={() => go(n.id)}
                className={`px-3.5 py-2 rounded-lg text-sm font-bold whitespace-nowrap transition ${
                  page === n.id ? "bg-teal-500 text-white" : "text-gray-500 hover:bg-gray-100"
                }`}>
                {n.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* 본문 */}
      {page === 0 && <Landing onGo={go} adult={adult} confirmAdult={confirmAdult} />}
      {page === 1 && <Page1 />}
      {page === 2 && <Page2 />}
      {page === 3 && <Page3 />}
      {page === 4 && (
        <Page4 saved={saved} addNumbers={addNumbers}
          receivedThisWeek={receivedThisWeek} setReceivedThisWeek={setReceivedThisWeek} />
      )}
      {page === 5 && <Page5 saved={saved} onGo={go} />}
      {page === 6 && <Page6 />}

      {/* 하단 고지 */}
      {page !== 0 && (
        <footer className="border-t border-gray-100 bg-white mt-4">
          <div className="max-w-6xl mx-auto px-6 py-6 text-[11px] text-gray-400 leading-relaxed">
            본 서비스는 19세 미만 청소년의 이용을 제한합니다 · 로또는 소액으로 건전하게 즐기세요<br />
            로딱이네 수치는 과거 데이터 분석 결과이며 미래 결과와 무관합니다 · 생성된 번호가 당첨을 보장하지 않습니다
          </div>
        </footer>
      )}
    </div>
  );
}
